import { useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Box, Chip, Stack, Typography, styled } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { CustomButton } from "@/components/custom/custom-button";
import CustomConfirmDialog from "@/components/custom/custom-confirm-dialog";
import { CustomErrorMessage } from "@/components/custom/custom-error-message";
import { showToast } from "@/redux/actions/toast-actions";
import { useProviders, useSetDefaultProvider } from "@/hooks/use-notifications";
import { PROVIDER_TYPES } from "../data";

const Card = styled(Box)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 12,
  padding: theme.spacing(2.5),
  backgroundColor: theme.palette.background.paper,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
}));

const ProviderRow = styled(Box, {
  shouldForwardProp: (p) => p !== "isDefault",
})(({ theme, isDefault }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.spacing(2),
  padding: theme.spacing(1.5),
  borderRadius: 8,
  border: `1px solid ${isDefault ? theme.palette.primary.main : theme.palette.divider}`,
  backgroundColor: isDefault ? `${theme.palette.primary.main}0a` : "transparent",
}));

const SectionLabel = styled(Typography)(({ theme }) => ({
  fontSize: "0.75rem",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: theme.palette.text.secondary,
}));

const Providers = () => {
  const dispatch = useDispatch();
  const [target, setTarget] = useState(null);

  const { data: providers, isLoading, isError } = useProviders();
  const mutation = useSetDefaultProvider();

  // The endpoint returns a flat list with a `type` discriminator; grouped here
  // so each type's providers render under their own heading.
  const grouped = useMemo(() => {
    const byType = {};
    (providers ?? []).forEach((p) => {
      const key = String(p.type ?? "").toLowerCase();
      if (!byType[key]) byType[key] = [];
      byType[key].push(p);
    });
    return byType;
  }, [providers]);

  if (isError) {
    return (
      <Card>
        <CustomErrorMessage message="Providers could not be loaded." />
      </Card>
    );
  }

  return (
    <Stack spacing={2}>
      {PROVIDER_TYPES.map(({ label, value }) => {
        const rows = grouped[value] ?? [];
        return (
          <Card key={value}>
            <SectionLabel>{label} providers</SectionLabel>

            {isLoading ? (
              <Typography variant="body2" color="text.secondary">
                Loading...
              </Typography>
            ) : rows.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No {label.toLowerCase()} providers configured.
              </Typography>
            ) : (
              <Stack spacing={1}>
                {rows.map((row) => (
                  <ProviderRow key={`${row.type}-${row.provider}`} isDefault={row.defaultProvider}>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      <Typography fontSize="0.875rem" fontWeight={500}>
                        {row.provider}
                      </Typography>
                      {row.defaultProvider && (
                        <Chip
                          icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                          label="Default"
                          size="small"
                          sx={{
                            height: 22,
                            fontWeight: 600,
                            fontSize: "0.7rem",
                            backgroundColor: "#ecfdf5",
                            color: "#065f46",
                          }}
                        />
                      )}
                    </Stack>

                    {!row.defaultProvider && (
                      <CustomButton
                        variantType="outline"
                        size="small"
                        onClick={() => setTarget(row)}
                        sx={{ height: 32, px: 2 }}
                      >
                        Make default
                      </CustomButton>
                    )}
                  </ProviderRow>
                ))}
              </Stack>
            )}
          </Card>
        );
      })}

      <CustomConfirmDialog
        open={Boolean(target)}
        onClose={() => setTarget(null)}
        title="Change default provider?"
        description={`All outgoing ${String(target?.type ?? "").toLowerCase()} will be sent through ${target?.provider}. The current default is replaced immediately.`}
        confirmLabel="Set as default"
        variant="warning"
        mutation={mutation}
        onConfirm={() =>
          mutation.mutate(
            { type: String(target.type).toLowerCase(), provider: target.provider },
            {
              onSuccess: (res) =>
                dispatch(showToast(res?.message ?? "Default provider updated", "success")),
              onError: (err) =>
                dispatch(showToast(err?.message ?? "Failed to update provider", "error")),
            },
          )
        }
      />
    </Stack>
  );
};

export default Providers;